/**
 * The backend stores `es` | `en` | `pt-br`, vue-i18n and Vueform use `pt-BR`: the two functions below
 * are the only converter. A language is never deduced from the society or its country.
 */
const SUPPORTED = ['es', 'en', 'pt-br']
const RELOAD_GUARD_KEY = 'locale_sync_reload'

/** The locale vue-i18n booted with (`src/i18n.ts` reads the same key); compared, not localStorage, so a refused write cannot loop. */
const runningLocale = typeof localStorage === 'undefined' ? 'es' : (localStorage.getItem('locale') || 'es')

let reloadPending = false
let seeded = false

/** `pt-BR` -> `pt-br`; `null` for anything unsupported. */
export function toApiLanguage(locale: string | null | undefined): string | null {
  if (!locale) return null
  const code = locale.trim().toLowerCase().replace('_', '-')
  const normalized = code === 'pt' ? 'pt-br' : code
  return SUPPORTED.includes(normalized) ? normalized : null
}

/** `pt-br` -> `pt-BR`; `null` for anything unsupported. */
export function fromApiLanguage(language: string | null | undefined): string | null {
  const code = toApiLanguage(language)
  return code === 'pt-br' ? 'pt-BR' : code
}

/** Saves the choice on the profile; resolves `false` (never rejects) when the backend refuses it. */
export async function saveUserLanguage(locale: string): Promise<boolean> {
  // Loaded on demand: `api.ts` pulls in the router, and this module is imported by the session loader.
  const { Api } = await import('@/utils/api')
  return new Promise((resolve) => {
    void Api.put(
      'users/me/language/',
      { language: toApiLanguage(locale) },
      () => {},
      () => resolve(true),
      () => resolve(false),
    )
  })
}

/** The switcher's action: save on the profile first, then switch and reload. Returns `false` (nothing changed) when saving fails. */
export async function changeUserLanguage(locale: string): Promise<boolean> {
  if (!(await saveUserLanguage(locale))) return false
  localStorage.setItem('locale', locale)
  window.location.reload()
  return true
}

/** Aligns the running locale with the profile; `true` when the page must reload (Vuetify/Vueform read it at mount). */
export function syncUserLocale(user: { language?: string | null } | undefined): boolean {
  const target = fromApiLanguage(user?.language)

  if (!target) {
    const stored = typeof localStorage === 'undefined' ? null : toApiLanguage(localStorage.getItem('locale'))
    if (stored && !seeded) {
      seeded = true
      void saveUserLanguage(stored)
    }
    return false
  }

  if (target === runningLocale) {
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
    return false
  }
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === target) return false

  localStorage.setItem('locale', target)
  sessionStorage.setItem(RELOAD_GUARD_KEY, target)
  reloadPending = true
  return true
}

/** Whether a locale change is waiting for a reload; reading it clears it. */
export function consumeLocaleReload(): boolean {
  const pending = reloadPending
  reloadPending = false
  return pending
}
